import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { useIsMounted } from '../libs';

export type UseResizeObserverSize = {
  width: number | undefined;
  height: number | undefined;
};

type BoxSizesKey = keyof Pick<
  ResizeObserverEntry,
  'borderBoxSize' | 'contentBoxSize' | 'devicePixelContentBoxSize'
>;

function extractSize(
  entry: ResizeObserverEntry,
  box: BoxSizesKey,
  sizeType: keyof ResizeObserverSize,
): number | undefined {
  if (!entry[box]) {
    if (box === 'contentBoxSize') {
      return entry.contentRect[sizeType === 'inlineSize' ? 'width' : 'height'];
    }

    return undefined;
  }

  return Array.isArray(entry[box])
    ? entry[box][0]?.[sizeType]
    : // @ts-ignore Support Firefox's non-standard behavior
      (entry[box][sizeType] as number);
}

const initialSize: UseResizeObserverSize = {
  width: undefined,
  height: undefined,
};

export type UseResizeObserverOptions<T extends HTMLElement = HTMLElement> = {
  /** The ref of the element to observe. */
  ref: RefObject<T>;
  /**
   * When using `onResize`, the hook doesn't re-render on element size changes; it delegates handling to the provided callback.
   * @default undefined
   */
  onResize?: (size: UseResizeObserverSize) => void;
  /**
   * The box model to use for the ResizeObserver.
   * @default 'content-box'
   */
  box?: 'border-box' | 'content-box' | 'device-pixel-content-box';

  /**
   * Indicates if the observing should be skipped or not.
   *
   * Could be usefull if you are using hook to observe child that is conditionally mounted (ex. footer in ActionSheet)
   */
  skip?: boolean;
};

/**
 * Custom hook that observes the size of an element using the [`ResizeObserver API`](https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver).
 * @template T - The type of the element to observe.
 * @param {UseResizeObserverOptions<T>} options - The options for the ResizeObserver.
 * @returns {UseResizeObserverSize} - The size of the observed element.
 * @public
 * @see [Documentation](https://usehooks-ts.com/react-hook/use-resize-observer)
 * @example
 * ```tsx
 * const myRef = useRef(null);
 * const { width = 0, height = 0 } = useResizeObserver({
 *   ref: myRef,
 *   box: 'content-box',
 * });
 *
 * <div ref={myRef}>Hello, world!</div>
 * ```
 *
 * @note
 * This component is essentially same as the one in `usehooks-ts` with expansion of `skip` parameter
 */
export function useResizeObserver<T extends HTMLElement = HTMLElement>(
  options: UseResizeObserverOptions<T>,
): UseResizeObserverSize {
  const { ref, box = 'content-box', skip } = options;
  const [{ width, height }, setSize] =
    useState<UseResizeObserverSize>(initialSize);
  const isMounted = useIsMounted();
  const previousSize = useRef<UseResizeObserverSize>({ ...initialSize });
  const onResize = useRef<((size: UseResizeObserverSize) => void) | undefined>(
    undefined,
  );

  onResize.current = options.onResize;

  useEffect(() => {
    if (skip || !ref.current) return;

    if (typeof window === 'undefined' || !('ResizeObserver' in window)) return;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;

      const boxProp =
        // eslint-disable-next-line no-nested-ternary
        box === 'border-box'
          ? 'borderBoxSize'
          : box === 'device-pixel-content-box'
            ? 'devicePixelContentBoxSize'
            : 'contentBoxSize';

      const newWidth = extractSize(entry, boxProp, 'inlineSize');
      const newHeight = extractSize(entry, boxProp, 'blockSize');

      const hasChanged =
        previousSize.current.width !== newWidth ||
        previousSize.current.height !== newHeight;

      if (hasChanged) {
        const newSize: UseResizeObserverSize = {
          width: newWidth,
          height: newHeight,
        };

        previousSize.current.width = newWidth;
        previousSize.current.height = newHeight;

        if (onResize.current) {
          onResize.current(newSize);
        } else {
          if (isMounted()) {
            setSize(newSize);
          }
        }
      }
    });

    observer.observe(ref.current, { box });

    return () => {
      observer.disconnect();
    };
  }, [box, ref, isMounted, skip]);

  return { width, height };
}
